import { Component, OnInit, Input } from '@angular/core';
import {
	RailroadClass,
	RailroadOvertimeBasis,
	RailroadRegularTimeBasis,
} from 'src/shared/api';
import {
	FormBuilder,
	Validators,
	NgControl,
	ValidatorFn,
} from '@angular/forms';
import {
	FormConfig,
	CustomFormAccessor,
} from 'src/shared/form-models/form-cva.component';
import { CustomValidators } from 'src/shared/validators/custom-validators';
import { buildArrayFromEnum } from 'src/shared/utilities/utility-functions';
import {
	displayOvertimeBasis,
	displayRailroadClass,
	displayRegularTimeBasis,
} from 'src/shared/enums/enums';
import { RailroadDetailGeneralInformation } from '../railroad-detail.models';
import { merge, Observable } from 'rxjs';
import { withLatestFrom, map, takeUntil } from 'rxjs/operators';

const railroadGeneralInformationFormConfig: FormConfig<RailroadDetailGeneralInformation> =
	{
		id: [''],
		companyName: ['', [Validators.required]],
		code: ['', [Validators.required]],
		class: ['', [Validators.required]],
		employerIdentificationNumber: [''],
		dailyVehicleFee: ['', [CustomValidators.isNonNegativeNumber]],
		weeklyVehicleFee: ['', [CustomValidators.isNonNegativeNumber]],
		monthlyVehicleFee: ['', [CustomValidators.isNonNegativeNumber]],
		billingEmail: [
			'',
			[Validators.email, Validators.required],
			[],
			{ updateOn: 'blur' },
		],
		divisionLabel: [''],
		includeWcrReportInInvoice: [''],
		dwrType: [''],
		contractNumber: [''],
		taskNumber: [''],
		regularTimeBasis: ['', Validators.required],
		overtimeBasis: ['', Validators.required],
		isIntegratedWithFlairDocs: [''],
		showWorkOrderNumberOnInvoice: [''],
		requireReportingForDuty: [''],
	};
@Component({
	selector: 'rpfsa-railroad-general-info',
	templateUrl: './general-info.component.html',
})
export class RailroadDetailGeneralInfoComponent
	extends CustomFormAccessor<RailroadDetailGeneralInformation>
	implements OnInit
{
	@Input() isExistingRailroad: boolean;

	areVehicleFeesRequired: boolean;

	readonly displayRegularTimeBasis = displayRegularTimeBasis;
	readonly displayOvertimeBasis = displayOvertimeBasis;
	readonly overtimeBasisTypes = buildArrayFromEnum(RailroadOvertimeBasis);
	readonly regularTimeBasisTypes = buildArrayFromEnum(
		RailroadRegularTimeBasis
	);
	readonly class = buildArrayFromEnum(RailroadClass);
	readonly displayClass = displayRailroadClass;

	constructor(public ngControl: NgControl, public fb: FormBuilder) {
		super(ngControl, fb, railroadGeneralInformationFormConfig);
	}

	ngOnInit() {
		const vehicleFees$ = [
			this.form.get('weeklyVehicleFee').valueChanges,
			this.form.get('monthlyVehicleFee').valueChanges,
		] as Observable<number>[];
		merge(...vehicleFees$)
			.pipe(
				withLatestFrom(...vehicleFees$),
				map<number[], boolean>((fees) => fees.some(Boolean)),
				takeUntil(this.destroyed)
			)
			.subscribe((vehicleFeeValue) => {
				if (vehicleFeeValue) {
					this.setVehicleFeeValidators([
						CustomValidators.isNonNegativeNumber,
						Validators.required,
					]);
				} else {
					this.setVehicleFeeValidators([
						CustomValidators.isNonNegativeNumber,
					]);
				}
			});
		super.ngOnInit();
	}

	writeValue(value: RailroadDetailGeneralInformation): void {
		if (value) {
			this.form.patchValue(value, { emitEvent: false });
			if (value.weeklyVehicleFee || value.monthlyVehicleFee) {
				this.setVehicleFeeValidators([
					CustomValidators.isNonNegativeNumber,
					Validators.required,
				]);
			} else {
				this.setVehicleFeeValidators([
					CustomValidators.isNonNegativeNumber,
				]);
			}
		}
	}

	private setVehicleFeeValidators(validators: ValidatorFn[]): void {
		this.form.get('dailyVehicleFee').setValidators(validators);
		this.form.get('weeklyVehicleFee').setValidators(validators);
		this.form.get('monthlyVehicleFee').setValidators(validators);
		this.form
			.get('dailyVehicleFee')
			.updateValueAndValidity({ emitEvent: false });
		this.form
			.get('weeklyVehicleFee')
			.updateValueAndValidity({ emitEvent: false });
		this.form
			.get('monthlyVehicleFee')
			.updateValueAndValidity({ emitEvent: false });

		this.areVehicleFeesRequired = validators.includes(Validators.required);
	}
}
